import { useSyncExternalStore, useMemo, useEffect, useState } from 'react';
import { store } from '@/services/storage';
import { supabase } from '@/lib/supabase';
import { toCompany } from '@/services/api';

export const useStoreState = () => useSyncExternalStore(store.subscribe, store.getState, store.getState);

// Colunas que o visitante anônimo também pode ler (batem com as permissões do banco)
const PUBLIC_COMPANY_COLS =
  'id,name,tagline,description,category_id,extra_category_ids,business_type,uf,city,services,products,portfolio,website,socials,verified,plan,sponsored,featured,status,created_at,updated_at,logo_url,cnpj_status';
const PUBLIC_OPP_COLS =
  'id,company_id,title,type,category_id,summary,description,requirements,uf,city,target_ufs,published_at,deadline,contact_method,status,sponsored,created_at,updated_at';

const toOpportunity = (row) => ({
  id: row.id,
  title: row.title,
  type: row.type,
  categoryId: row.category_id,
  summary: row.summary,
  description: row.description,
  requirements: row.requirements || [],
  companyId: row.company_id,
  location: { uf: row.uf, city: row.city },
  targetUfs: row.target_ufs || [],
  publishedAt: row.published_at,
  deadline: row.deadline,
  contactMethod: row.contact_method,
  status: row.status,
  sponsored: !!row.sponsored,
  moderationFlags: row.moderation_flags || [],
  isDemo: false,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

// Lê o perfil do usuário logado direto do Supabase
async function fetchUser(session) {
  if (!session?.user) return null;
  const { data, error } = await supabase
    .from('profiles')
    .select('id, name, role, status')
    .eq('id', session.user.id)
    .single();
  if (error || !data) return null;
  return {
    id: data.id,
    name: data.name,
    email: session.user.email,
    role: data.role === 'admin' ? 'admin' : 'empresario',
    status: data.status === 'suspended' ? 'suspensa' : 'ativa',
    companyId: null,
  };
}

// Empresa vinculada ao dono logado
async function fetchMyCompany(session) {
  if (!session?.user) return null;
  const { data, error } = await supabase
    .from('companies')
    .select('*')
    .eq('owner_id', session.user.id)
    .maybeSingle();
  if (error || !data) return null;
  return toCompany(data);
}

// Vitrine real: só empresas e oportunidades ativas
async function fetchCatalog() {
  const [companiesRes, oppsRes] = await Promise.all([
    supabase.from('companies').select(PUBLIC_COMPANY_COLS).eq('status', 'ativa'),
    supabase.from('opportunities').select(PUBLIC_OPP_COLS).eq('status', 'ativa'),
  ]);
  return {
    companies: (companiesRes.data || []).map(toCompany),
    opportunities: (oppsRes.data || []).map(toOpportunity),
  };
}

// Todas as oportunidades da própria empresa, em qualquer status
async function fetchMyOpportunities(companyId) {
  const { data, error } = await supabase
    .from('opportunities')
    .select('*')
    .eq('company_id', companyId)
    .order('published_at', { ascending: false });
  if (error || !data) return [];
  return data.map(toOpportunity);
}

function useSupabaseSession() {
  const [state, setState] = useState({
    user: null,
    myCompany: null,
    myOpportunities: [],
    catalog: null,
    loading: true,
  });

  useEffect(() => {
    let active = true;
    const load = async (session) => {
      const user = await fetchUser(session);
      if (!user) {
        if (active) setState({ user: null, myCompany: null, myOpportunities: [], catalog: null, loading: false });
        return;
      }
      const [myCompany, catalog] = await Promise.all([fetchMyCompany(session), fetchCatalog()]);
      const myOpportunities = myCompany ? await fetchMyOpportunities(myCompany.id) : [];
      if (active) {
        setState({
          user: { ...user, companyId: myCompany?.id ?? null },
          myCompany,
          myOpportunities,
          catalog,
          loading: false,
        });
      }
    };
    supabase.auth.getSession().then(({ data }) => load(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => load(session));
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  return state;
}

export function useData() {
  const s = useStoreState();
  const { user, myCompany, myOpportunities, catalog, loading } = useSupabaseSession();

  return useMemo(() => {
    // Logado: dados reais. Visitante: exemplos (seed).
    const live = !!user;
    const baseCompanies = live ? catalog?.companies || [] : s.companies;
    const baseOpps = live ? catalog?.opportunities || [] : s.opportunities;

    const companies = myCompany && live
      ? [myCompany, ...baseCompanies.filter((c) => c.id !== myCompany.id)]
      : baseCompanies;

    const opportunities = live
      ? [...myOpportunities, ...baseOpps.filter((o) => !myOpportunities.some((m) => m.id === o.id))]
      : baseOpps;

    const companyById = Object.fromEntries(companies.map((c) => [c.id, c]));
    const categoryById = Object.fromEntries(s.categories.map((c) => [c.id, c]));
    const publicCompanies = companies.filter((c) => c.status === 'ativa');
    const publicOpportunities = opportunities.filter(
      (o) => o.status === 'ativa' && companyById[o.companyId]?.status === 'ativa'
    );

    return {
      ...s,
      companies,
      opportunities,
      companyById,
      categoryById,
      publicCompanies,
      publicOpportunities,
      user,
      authLoading: loading,
      myCompany,
    };
  }, [s, user, myCompany, myOpportunities, catalog, loading]);
}